import { datadogLogs } from '@datadog/browser-logs';
import { Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import {
  fetchAuthData,
  loginWithOktaBypass,
  loginWithOktaToken,
  useAuth,
} from '../../auth';
import { environment } from '../../environment';
import { oktaAuth } from '../okta/auth';
import { NoAuthPage } from './NoAuthPage';

export function LoginPage() {
  const { auth, setAuth } = useAuth();

  // On the Login Page we want to check for an okta session.
  // If a backend session exists then we don't care about the okta session.
  // If an okta session exists then attempt to exchange it for a backend session.
  // If there is no backend session or okta session then redirect to the okta sign in.
  useEffect(() => {
    // signInWithRedirect() only settles once it has fetched Okta's
    // /.well-known/openid-configuration and handed the browser off to the
    // authorize URL, so anything that interrupts that window rejects it. A
    // competing navigation is enough: when the backend session expires,
    // AuthProvider's handleExpiredSession hard-redirects to /login while the
    // router is already rendering this page, and the document teardown aborts
    // the in-flight well-known body read. Okta relabels that aborted read as
    // "AuthApiError: Could not parse server response" rather than surfacing the
    // underlying fetch failure (SC-4028), and because the call was never awaited
    // the rejection escaped as an unhandled promise rejection into RUM.
    //
    // There is nothing to recover here: either the redirect happened or the page
    // is already leaving. Log at error level so a genuine redirect failure, which
    // strands the user on this page with no way forward, stays visible.
    async function redirectToOktaSignIn() {
      try {
        await oktaAuth.signInWithRedirect();
      } catch (error) {
        datadogLogs.logger.error('Okta sign-in redirect did not complete', {
          error,
        });
      }
    }

    async function checkAndExchangeToken() {
      if (auth) {
        return; // Already authenticated
      }

      // A rejection here means the request never completed: usually the browser
      // aborted it because the document is being torn down by a navigation,
      // otherwise a dropped connection. Safari surfaces both as
      // "TypeError: Load failed" (SC-4092). Either way there is no backend
      // session to use, so treat it like a 401 and continue to the Okta path
      // rather than leaving the rejection unhandled.
      const backendAuth = await fetchAuthData().catch((error) => {
        datadogLogs.logger.info(
          'Unable to check for a backend session on login, continuing to Okta',
          { error },
        );
        return null;
      });
      if (backendAuth) {
        setAuth(backendAuth);
        return;
      }

      // Local dev escape hatch: if the server has DEV_LOGIN_PROFILE_ID set,
      // /web-dev-login issues a session without Okta. Lets portless hostnames
      // (and any other non-registered origin) log in in local dev.
      if (environment.VITE_ENVIRONMENT === 'local') {
        const devAuth = await loginWithOktaBypass();
        if (devAuth) {
          setAuth(devAuth);
          return;
        }
      }

      const authState = oktaAuth.authStateManager.getAuthState();
      const idToken = authState?.idToken?.idToken;
      if (idToken && authState?.isAuthenticated) {
        // Have Okta tokens - exchange for backend session
        try {
          await loginWithOktaToken(idToken, setAuth);
        } catch (error) {
          datadogLogs.logger.error(
            'Error exchanging Okta token for backend session',
            { error },
          );
          // Exchange failed, redirect to Okta
          await redirectToOktaSignIn();
        }
      } else {
        // No Okta tokens - redirect to Okta login
        await redirectToOktaSignIn();
      }
    }

    checkAndExchangeToken();
  }, [auth, setAuth]);

  if (auth?.type === 'org') {
    return <Navigate to="/plan" />;
  } else if (auth?.type === 'admin') {
    return <Navigate to="/admin" />;
  }

  return <NoAuthPage />;
}
